(() => {
  const catalogPromises = new Map();
  let lastLoadInfo = null;

  const appendQuery = (url, key, value) => {
    if (!url) return '';
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
  };

  const normaliseUrl = (url) => {
    if (!url) return '';
    try {
      return new URL(url, window.location.origin).toString();
    } catch (error) {
      return url;
    }
  };

  const inferManifestUrl = (catalogUrl) => {
    if (!catalogUrl) return '';
    try {
      const url = new URL(catalogUrl, window.location.origin);
      url.pathname = url.pathname.replace(/pattern-catalog(?:-[a-f0-9]+)?\.json$/i, 'pattern-catalog-manifest.json');
      url.search = '';
      return url.toString();
    } catch (error) {
      return String(catalogUrl).replace(
        /pattern-catalog(?:-[a-f0-9]+)?\.json(?:\?.*)?$/i,
        'pattern-catalog-manifest.json',
      );
    }
  };

  const inferCanonicalCatalogUrl = (catalogUrl) => {
    if (!catalogUrl) return '';
    try {
      const url = new URL(catalogUrl, window.location.origin);
      url.pathname = url.pathname.replace(/pattern-catalog(?:-[a-f0-9]+)?\.json$/i, 'pattern-catalog.json');
      url.search = '';
      return url.toString();
    } catch (error) {
      return String(catalogUrl).replace(/pattern-catalog(?:-[a-f0-9]+)?\.json(?:\?.*)?$/i, 'pattern-catalog.json');
    }
  };

  const fetchJson = async (url, options = {}) => {
    const response = await fetch(url, {
      cache: options.cache || 'default',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  };

  const readManifest = async (manifestUrl, catalogUrl) => {
    const url = normaliseUrl(manifestUrl || inferManifestUrl(catalogUrl));
    if (!url) return null;

    try {
      return await fetchJson(appendQuery(url, '_kickis_manifest_check', Date.now()), { cache: 'no-store' });
    } catch (error) {
      console.warn('Kunde inte läsa mönsterkatalogens versionsfil.', error);
      return null;
    }
  };

  const manifestVersion = (manifest) => {
    if (!manifest || typeof manifest !== 'object') return '';
    return String(manifest.hash || manifest.version || manifest.catalogGeneratedAt || manifest.generatedAt || manifest.count || '').trim();
  };

  const resolveCatalogUrl = (catalogUrl, manifest) => {
    const catalogAsset = String(manifest?.catalogAsset || '').trim();
    if (!catalogAsset) return normaliseUrl(catalogUrl);

    try {
      const url = new URL(catalogUrl, window.location.origin);
      url.pathname = url.pathname.replace(/pattern-catalog(?:-[a-f0-9]+)?\.json$/i, catalogAsset);
      url.search = '';
      return url.toString();
    } catch (error) {
      return String(catalogUrl).replace(/pattern-catalog(?:-[a-f0-9]+)?\.json(?:\?.*)?$/i, catalogAsset);
    }
  };

  const catalogPatterns = (catalog) => (Array.isArray(catalog) ? catalog : (catalog?.patterns || []));

  const expectedCatalogCount = (manifest) => {
    const count = Number(manifest?.count);
    return Number.isInteger(count) && count >= 0 ? count : null;
  };

  const catalogMatchesManifest = (catalog, manifest) => {
    const expectedCount = expectedCatalogCount(manifest);
    return expectedCount === null || catalogPatterns(catalog).length === expectedCount;
  };

  const fetchVerifiedCatalog = async (catalogUrl, manifest, version, force = false) => {
    const resolvedCatalogUrl = resolveCatalogUrl(catalogUrl, manifest);
    const fetchUrl = version
      ? appendQuery(resolvedCatalogUrl, '_kickis_catalog_hash', version.slice(0, 32))
      : appendQuery(resolvedCatalogUrl, '_kickis_catalog_check', Date.now());

    let catalog = await fetchJson(fetchUrl, { cache: force ? 'no-store' : 'default' });
    if (!catalogMatchesManifest(catalog, manifest)) {
      catalog = await fetchJson(appendQuery(resolvedCatalogUrl, '_kickis_catalog_retry', Date.now()), { cache: 'no-store' });
    }

    if (!catalogMatchesManifest(catalog, manifest)) {
      const canonicalCatalogUrl = inferCanonicalCatalogUrl(catalogUrl);
      catalog = await fetchJson(appendQuery(canonicalCatalogUrl, '_kickis_catalog_manifest', version || Date.now()), {
        cache: 'no-store',
      });
    }

    if (!catalogMatchesManifest(catalog, manifest)) {
      const expectedCount = expectedCatalogCount(manifest);
      throw new Error(
        `Mönsterkatalogen innehåller ${catalogPatterns(catalog).length} poster men manifestet kräver ${expectedCount}.`,
      );
    }

    lastLoadInfo = {
      version,
      expectedCount: expectedCatalogCount(manifest),
      actualCount: catalogPatterns(catalog).length,
      catalogUrl: resolvedCatalogUrl,
      checkedAt: new Date().toISOString(),
    };
    return catalog;
  };

  const loadCatalog = async (catalogUrl, options = {}) => {
    const baseUrl = normaliseUrl(catalogUrl);
    if (!baseUrl) return { patterns: [] };

    const manifest = await readManifest(options.manifestUrl, baseUrl);
    const version = manifestVersion(manifest);
    const resolvedCatalogUrl = resolveCatalogUrl(baseUrl, manifest);
    const promiseKey = `${resolvedCatalogUrl}::${version || 'unversioned'}`;

    if (options.force === true) catalogPromises.delete(promiseKey);

    if (!catalogPromises.has(promiseKey)) {
      const pendingCatalog = fetchVerifiedCatalog(baseUrl, manifest, version, options.force === true)
        .catch((error) => {
          catalogPromises.delete(promiseKey);
          throw error;
        });
      catalogPromises.set(promiseKey, pendingCatalog);
    }

    return catalogPromises.get(promiseKey);
  };

  window.KickisPatternCatalog = {
    load: loadCatalog,
    inferManifestUrl,
    getLastLoadInfo() {
      return lastLoadInfo ? { ...lastLoadInfo } : null;
    },
    clearMemoryCache() {
      catalogPromises.clear();
    },
  };
})();
